import { ParentProps, createSignal, onMount } from "solid-js";
import { DatesContext } from "./context";
import { DateCategory, DateEvent } from "../../types/date";
import { getDateData } from "../../repositories/event";
import dayjs from "dayjs";

export function DatesProvider(props: ParentProps) {

	const [dates, setDates] = createSignal<DateEvent[]>([]);
	const [event, setEvent] = createSignal<DateEvent>();
	const [category, setCategory] = createSignal<string>("");
	const [categories, setCategories] = createSignal<DateCategory[]>([]);
	const [state, setState] = createSignal<"loading" | "idle" | "error">("loading");
	const [listRef, setListRef] = createSignal<HTMLDivElement | null>(null);
	const [openContentDialog, setOpenContentDialog] = createSignal(false);

	onMount(async () => {

		setState("loading");

		try {
			const { dateCategories, dateEvents } = await getDateData();

			setCategories(dateCategories);
			setDates(dateEvents);

			const url = new URL(window.location.href);
			const searchParams = new URLSearchParams(url.search);

			const queryCategory = searchParams.get("category") || "";

			if (queryCategory !== "") {
				url.searchParams.set("category", queryCategory);
				window.history.pushState({}, "", url);
			}

			setCategory(queryCategory);
			
			setTimeout(() => setState("idle"), 400);
		} catch (error) {
			console.error(error);
			setState("error");
		}
	});

	const onSelectCategory = (value: string) => {
		const url = new URL(window.location.href);

		setCategory(value);

		if (value !== "") {
			url.searchParams.set("category", value);
		} else {
			url.searchParams.delete("category");
		}

		window.history.pushState({}, "", url);
	};

	const filteredEvents = () => {
		const _dates = dates();
		const _category = category();

		if (!_category) {
			return _dates;
		}

		return _dates.filter(date => date.category.id === _category);
	};

	const oncomingEvents = () => {
		const _dates = dates();
		const now = new Date().getTime();

		return _dates
			.filter(date => date.startTime.getTime() > now)
			.sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
			.slice(0, 3);
	};

	const formatDate = (event?: DateEvent) => {

		if(!event) return "";

		let dateFormat = "dd D MMMM";

		const startTime = dayjs(event.startTime);
		const endTime = dayjs(event.endTime);

		if(startTime.isSame(endTime, "day")) {
			let formattedStart = startTime.format(dateFormat);
			formattedStart = formattedStart.charAt(0).toUpperCase() + formattedStart.slice(1);
			
			return formattedStart;
		}

		if (event.endTime) {
			if (!startTime.isSame(endTime, "year")) {
				dateFormat += " YYYY";
			}

			let formattedStart = startTime.format(dateFormat);
			let formattedEnd = endTime.format(dateFormat);

			// uppercase first letter of the week day
			formattedStart = formattedStart.charAt(0).toUpperCase() + formattedStart.slice(1);
			formattedEnd = formattedEnd.charAt(0).toUpperCase() + formattedEnd.slice(1);

			return `${formattedStart} t/m ${formattedEnd}`;
		}

		return startTime.format(dateFormat);
	};

	const formatTime = (event?: DateEvent) => {

		if(!event) {
			return "";
		}

		const format = "HH:mm";

		const start = dayjs(event.startTime).format(format);

		if(!event.endTime) {
			return start;
		}

		const end = dayjs(event.endTime).format(format);
		return `${start} - ${end}`;
	};

	return (
		<DatesContext.Provider
			value={{
				events: filteredEvents,
				oncomingEvents,
				setCategory: onSelectCategory,
				category,
				categories,
				event,
				setEvent,
				state,
				listRef,
				setListRef,
				formatDate,
				formatTime,
				openContentDialog,
				setOpenContentDialog
			}}
		>
			{props.children}
		</DatesContext.Provider>
	);
}